import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { TextField, Box, Alert, Typography, Paper, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AuthHeaderActions from '../../components/auth/AuthHeaderActions';
import SalovareLogo from '../../components/common/SalovareLogo';
import StoreAuthLogo, { getStoreAuthBackgroundSx } from '../../components/auth/StoreAuthBranding';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import i18n from '../../i18n';
import AuthService from '../../services/authService';
import PasswordField from '../../components/auth/PasswordField';
import { brandBackdropSx, brandCardSx } from '../../theme/brandSurfaces';
import { useOptionalStore } from '../../context/StoreContext';

const backgroundStyle = {
  minHeight: { xs: '100dvh', sm: '100vh' },
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  ...brandBackdropSx,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  py: { xs: 2, sm: 0 },
  px: 0,
  touchAction: 'pan-y',
};

const cardStyle = {
  padding: { xs: '2rem 1.5rem', sm: '2.5rem 2rem' },
  ...brandCardSx,
  minWidth: { xs: 300, sm: 340 },
  maxWidth: { xs: 350, sm: 380 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { loginTeamMember, loginAppAdmin, loginCustomer } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeContext = useOptionalStore();
  
  const [form, setForm] = useState({ 
    email: searchParams.get('email') || '', 
    password: '', 
    confirmPassword: '' 
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const encodedToken = searchParams.get('token');
  const email = searchParams.get('email');
  const language = searchParams.get('language');
  const userType = searchParams.get('userType') || 'TeamMember';
  const loginLogoUrl = userType === 'Customer' ? storeContext?.storeOverview?.brandingImages?.loginLogoUrl : null;
  const loginBackgroundUrl = userType === 'Customer'
    ? storeContext?.storeOverview?.brandingImages?.loginBackgroundUrl
    : null;
  const storeName = storeContext?.storeName;
  
  // Decode the base64 token
  const token = encodedToken ? atob(encodedToken) : null;

  useEffect(() => {
    if (!token) {
      setError('resetPassword.missingToken');
    }
    if (!email) {
      setError('resetPassword.missingEmail');
    }
    
    // Set the language if provided
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [token, email, language]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.password || !form.confirmPassword) {
      setError('resetPassword.required');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('resetPassword.passwordMismatch');
      return;
    }

    if (form.password.length < 8) {
      setError('resetPassword.passwordTooShort');
      return;
    }

    // Check for at least one digit
    if (!/\d/.test(form.password)) {
      setError('resetPassword.passwordRequireDigit');
      return;
    }

    // Check for at least one lowercase letter
    if (!/[a-z]/.test(form.password)) {
      setError('resetPassword.passwordRequireLowercase');
      return;
    }

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(form.password)) {
      setError('resetPassword.passwordRequireUppercase');
      return;
    }

    // Check for at least one non-alphanumeric character
    if (!/[^a-zA-Z0-9]/.test(form.password)) {
      setError('resetPassword.passwordRequireNonAlphanumeric');
      return;
    }

    setLoading(true);

    try {
      // Reset the password using the appropriate service method
      if (userType === 'AppAdmin') {
        await AuthService.resetPasswordAppAdmin(form.email, token, form.password);
      } else if (userType === 'Customer') {
        await AuthService.resetPasswordCustomer(form.email, token, form.password);
      } else {
        await AuthService.resetPasswordTeamMember(form.email, token, form.password);
      }

      setSuccess(true);

      // Auto-login with new credentials using the appropriate login method
      let loginResult;
      if (userType === 'AppAdmin') {
        loginResult = await loginAppAdmin({
          email: form.email,
          password: form.password
        });
      } else if (userType === 'Customer') {
        loginResult = await loginCustomer({
          email: form.email,
          password: form.password
        });
      } else {
        loginResult = await loginTeamMember({
          email: form.email,
          password: form.password
        });
      }

      if (loginResult && loginResult.success) {
        // Redirect based on user type
        if (userType === 'Customer') {
          const storeId = localStorage.getItem('currentStoreId');
          const redirectPath = storeId 
            ? buildCustomerRoute(storeId, '')
            : '/not-found';
          navigate(redirectPath, { replace: true });
        } else {
          navigate('/management/appointments', { replace: true });
        }
      } else {
        // If auto-login fails, redirect to appropriate login page
        let loginPath;
        if (userType === 'AppAdmin') {
          loginPath = '/management/app-admin/login';
        } else if (userType === 'TeamMember') {
          loginPath = '/management/team-member/login';
        } else if (userType === 'Customer') {
          loginPath = '/login';
        } else {
          loginPath = '/management/team-member/login'; // Default
        }
        navigate(loginPath, { 
          state: { 
            message: 'resetPassword.loginRedirect',
            email: form.email 
          } 
        });
      }

    } catch (error) {
      setError('resetPassword.failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <Box sx={{ ...backgroundStyle, ...getStoreAuthBackgroundSx(loginBackgroundUrl) }}>
        <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24 }}>
          <AuthHeaderActions />
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, py: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', maxWidth: 380, justifyContent: 'flex-end', alignItems: 'center', px: 3, pt: 2, pb: 2, minHeight: 56, boxSizing: 'border-box' }}>
            <AuthHeaderActions />
          </Box>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { xs: 0, sm: 0 } }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <Paper elevation={8} sx={cardStyle}>
            {loginLogoUrl ? (
              <StoreAuthLogo imageUrl={loginLogoUrl} storeName={storeName} />
            ) : (
              <SalovareLogo />
            )}
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {t(error)}
            </Alert>
            <PrimaryActionButton
              fullWidth
              onClick={() => {
                const loginPath = userType === 'Customer' ? '/login' : 
                                 userType === 'AppAdmin' ? '/management/app-admin/login' : 
                                 '/management/team-member/login';
                navigate(loginPath);
              }}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              {t('resetPassword.backToLogin')}
            </PrimaryActionButton>
          </Paper>
        </motion.div>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...backgroundStyle, ...getStoreAuthBackgroundSx(loginBackgroundUrl) }}>
      <Box sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 24, right: 24 }}>
        <AuthHeaderActions />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, py: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', maxWidth: 380, justifyContent: 'flex-end', alignItems: 'center', px: 3, pt: 2, pb: 2, minHeight: 56, boxSizing: 'border-box' }}>
          <AuthHeaderActions />
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { xs: 0, sm: 0 } }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <Paper elevation={8} sx={cardStyle}>
          {loginLogoUrl ? (
            <StoreAuthLogo imageUrl={loginLogoUrl} storeName={storeName} />
          ) : (
            <SalovareLogo />
          )}
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
            {t('resetPassword.title')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {t(error)}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {t('resetPassword.success')}
            </Alert>
          )}
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('resetPassword.email')}
              name="email"
              autoComplete="email"
              value={form.email}
              disabled
              sx={{ mb: 2, borderRadius: 2 }}
              InputProps={{
                style: { borderRadius: 12 },
              }}
            />
            <PasswordField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('resetPassword.newPassword')}
              id="password"
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange}
              sx={{ mb: 2, borderRadius: 2 }}
              InputProps={{
                style: { borderRadius: 12 },
              }}
            />
                         <PasswordField
               margin="normal"
               required
               fullWidth
               name="confirmPassword"
               label={t('resetPassword.confirmPassword')}
               id="confirmPassword"
               autoComplete="new-password"
               value={form.confirmPassword}
               onChange={handleChange}
               sx={{ mb: 1, borderRadius: 2 }}
               InputProps={{
                 style: { borderRadius: 12 },
               }}
             />
             <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
               {t('resetPassword.passwordRequirements')}
             </Typography>
            <PrimaryActionButton
              type="submit"
              fullWidth
              sx={{
                mt: 2,
                mb: 1,
                py: 1.5,
                fontSize: '1.1rem',
              }}
              disabled={loading}
              endIcon={loading && <CircularProgress size={22} color="inherit" />}
            >
              {loading ? t('resetPassword.resetting') : t('resetPassword.resetPassword')}
            </PrimaryActionButton>
          </Box>
        </Paper>
      </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
